import type { Charger } from '../atoms/chargers';

export const CHART_GRID_LEFT = '7%';
export const CHART_GRID_RIGHT = '4%';
export const COMPACT_LABEL_THRESHOLD = 20;
const SLOT_BAR_WIDTH_PERCENT = 60;

export const stateMeta: Array<{ label: string; color: string }> = [
  { label: 'Fault', color: 'firebrick' },
  { label: 'Empty', color: 'grey' },
  { label: 'Wait', color: 'chocolate' },
  { label: 'Charging', color: '#eab308' },
  { label: 'Done', color: 'seagreen' },
  { label: 'Testing', color: 'slateblue' },
  { label: 'Override', color: '#f8fafc' },
];

export function getBarColor(value: number, isSlotAvailable: boolean, viewType: string): string {
  if (!isSlotAvailable) return '#9ca3af';
  if (value < 2) return 'firebrick';
  if (value > 25) return 'seagreen';
  return viewType === 'CPower' ? 'goldenrod' : 'teal';
}

export function getStackColor(state: number, isSlotAvailable: boolean): string {
  if (!isSlotAvailable) return '#9ca3af';
  return stateMeta[state]?.color || 'gray';
}

export interface ChartOptionParams {
  viewType: 'CPower' | 'CEnergy';
  visiblePlugNums: number[];
  cData: Array<number | null>;
  cStateData: number[];
  chargingData: Charger;
  assignedPlugNums: Set<number>;
  plugNumToAvailable: Record<number, boolean>;
  plugNumToReachable: Record<number, boolean>;
  scaleMode: 'auto' | '10' | '1000';
  lastChargedRangeByPlug?: Record<number, string>;
}

export function buildChartOption(params: ChartOptionParams) {
  const {
    viewType, visiblePlugNums, cData, cStateData, chargingData,
    assignedPlugNums, plugNumToAvailable, plugNumToReachable,
    scaleMode, lastChargedRangeByPlug = {},
  } = params;
  // V/A ride the same WebSocket payload as CPower (parallel arrays on
  // chargingData), so the tooltip reads them directly instead of issuing
  // per-plug GET /api/plugs/devices/:nodeId/power requests every 5–15s.
  const cVoltage = Array.isArray(chargingData.CVoltage) ? chargingData.CVoltage : [];
  const cCurrent = Array.isArray(chargingData.CCurrent) ? chargingData.CCurrent : [];

  const compactLabels = visiblePlugNums.length > COMPACT_LABEL_THRESHOLD;

  const cPowerValues = visiblePlugNums
    .map((n) => cData[n - 1])
    .map((v) => (v == null ? 0 : Number(v)))
    .filter((v) => Number.isFinite(v));
  const maxCpower = cPowerValues.length > 0 ? Math.max(0, ...cPowerValues) : 0;
  const autoScaleMax = maxCpower <= 10 ? 10 : Math.ceil((maxCpower * 1.15) / 10) * 10;
  const cPowerScaleMax = scaleMode === '1000' ? 1000 : scaleMode === '10' ? 10 : autoScaleMax;
  const autoStateBand = Math.max(1, Math.min(120, Math.round(cPowerScaleMax * 0.08)));
  const cPowerStateMin = scaleMode === '1000' ? -100 : scaleMode === '10' ? -1 : -autoStateBand;
  const cPowerScaleMin = scaleMode === '1000' ? -500 : scaleMode === '10' ? -5 : Math.min(-5, cPowerStateMin * 2.5);
  const yAxisMax = viewType === 'CPower' ? cPowerScaleMax : 10;
  const yAxisMin = viewType === 'CPower' ? cPowerScaleMin : -5;
  const offlineOutlineIndices = visiblePlugNums
    .map((plugNum, idx) => ({ plugNum, idx }))
    .filter(({ plugNum }) => assignedPlugNums.has(plugNum) && plugNumToReachable[plugNum] === false)
    .map(({ idx }) => idx);

  return {
    backgroundColor: 'transparent',
    title: {
      text: `Charging: ${viewType === 'CPower' ? 'Power' : 'Energy'} per plug`,
      textStyle: { color: 'grey' },
    },
    xAxis: {
      type: 'category',
      data: visiblePlugNums.map((n) => `${n}`),
      axisLabel: { show: false },
    },
    yAxis: {
      type: 'value',
      name: viewType === 'CPower' ? 'Watts' : 'KWh',
      max: yAxisMax,
      min: yAxisMin,
      axisLabel: {
        color: 'text.primary',
        formatter: (value: number) => (value >= 0 ? value : ''),
      },
    },
    series: [
      {
        name: 'Bar',
        data: visiblePlugNums.map((plugNum) => {
          const assigned = assignedPlugNums.has(plugNum);
          const avail = assigned ? !!plugNumToAvailable[plugNum] : false;
          const reachable = plugNumToReachable[plugNum];
          const raw = cData[plugNum - 1];
          const val = raw == null ? 0 : Number(raw);
          return {
            value: val,
            itemStyle: {
              color: getBarColor(val, avail, viewType),
              ...(assigned && reachable === false ? { opacity: 0.6 } : {}),
            },
          };
        }),
        type: 'bar',
        barWidth: `${SLOT_BAR_WIDTH_PERCENT}%`,
        showBackground: true,
        backgroundStyle: { color: 'rgba(180, 180, 180, 0.2)' },
        stack: 'total',
        emphasis: { scale: true, itemStyle: { borderColor: 'auto' } },
      },
      {
        name: 'State',
        data: visiblePlugNums.map((plugNum) => {
          const assigned = assignedPlugNums.has(plugNum);
          const avail = assigned ? !!plugNumToAvailable[plugNum] : false;
          const state = cStateData[plugNum - 1] ?? 1;
          return {
            value: viewType === 'CPower' ? cPowerStateMin : -1,
            itemStyle: { color: getStackColor(state, avail) },
          };
        }),
        type: 'bar',
        barWidth: `${SLOT_BAR_WIDTH_PERCENT}%`,
        stack: 'total',
        label: {
          show: true,
          position: 'bottom',
          formatter: (p: any) => {
            const idx = p?.dataIndex ?? 0;
            const plugNum = visiblePlugNums[idx] ?? idx + 1;
            if (compactLabels) return `${plugNum}`;
            const assigned = assignedPlugNums.has(plugNum);
            const avail = assigned ? !!plugNumToAvailable[plugNum] : false;
            const reachable = plugNumToReachable[plugNum];
            const isOffline = assigned && reachable === false;
            if (isOffline) return `${plugNum} Offline`;
            if (!avail) return `${plugNum} Unavailable`;
            const state = cStateData[plugNum - 1] ?? 1;
            return `${plugNum} ${stateMeta[state]?.label ?? ''}`.trim();
          },
          color: '#fff',
        },
        emphasis: { disabled: true },
        tooltip: { show: false },
      },
      {
        name: 'OfflineOutline',
        type: 'custom',
        silent: true,
        tooltip: { show: false },
        z: 30,
        renderItem: (_params: any, api: any) => {
          const idx = Number(api.value(0));
          const xCenter = api.coord([idx, 0])[0];
          const yTop = api.coord([idx, yAxisMax])[1];
          const yBottom = api.coord([idx, yAxisMin])[1];
          const bandWidth = Math.abs(api.size([1, 0])[0]);
          const rectWidth = bandWidth * (SLOT_BAR_WIDTH_PERCENT / 100);
          return {
            type: 'rect',
            shape: {
              x: xCenter - rectWidth / 2,
              y: Math.min(yTop, yBottom),
              width: rectWidth,
              height: Math.abs(yBottom - yTop),
            },
            style: {
              fill: 'transparent',
              stroke: 'firebrick',
              lineWidth: 2,
              lineDash: [5, 4],
              opacity: 0.95,
            },
          };
        },
        data: offlineOutlineIndices.map((idx) => [idx]),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (tooltipParams: any) => {
        const barParam = tooltipParams.find((p: any) => p.seriesName === 'Bar');
        const dataIndex = barParam?.dataIndex ?? 0;
        const plugNum = visiblePlugNums[dataIndex] ?? dataIndex + 1;
        const power = barParam?.data?.value ?? 0;
        const rawPower = Array.isArray(chargingData.CPower) ? chargingData.CPower[plugNum - 1] : undefined;
        const assigned = assignedPlugNums.has(plugNum);
        const avail = assigned ? !!plugNumToAvailable[plugNum] : false;
        const reachable = plugNumToReachable[plugNum];
        const isOffline = assigned && reachable === false;
        const statusStr = isOffline ? 'Offline' : !avail ? 'Unavailable' : 'Online';
        const stateValue = cStateData[plugNum - 1] ?? 1;
        const stateStr = stateMeta[stateValue]?.label ?? 'Unknown';
        const rawVoltage = cVoltage[plugNum - 1];
        const rawCurrent = cCurrent[plugNum - 1];
        const voltage = rawVoltage != null && Number.isFinite(Number(rawVoltage))
          ? `${Number(rawVoltage).toFixed(1)} V`
          : '\u2014';
        const current = rawCurrent != null && Number.isFinite(Number(rawCurrent))
          ? `${Number(rawCurrent).toFixed(2)} A`
          : '\u2014';
        const powerUnknown = viewType === 'CPower' && rawPower == null && Number(power) === 0;
        const powerStr = viewType === 'CPower'
          ? (powerUnknown ? '-' : `${Number(power).toFixed(1)} W`)
          : `${Number(power).toFixed(2)} kWh`;
        return [
          `<div style="font-weight:600">Plug ${plugNum} (${statusStr}) - ${stateStr}</div>`,
          `Power: ${powerStr}`,
          `Voltage: ${voltage}`,
          `Current: ${current}`,
          `Last charged: ${lastChargedRangeByPlug[plugNum] ?? '--'}`,
        ].join('<br/>');
      },
    },
    grid: {
      left: CHART_GRID_LEFT,
      right: CHART_GRID_RIGHT,
      bottom: '12%',
      containLabel: false,
    },
  };
}
